import * as THREE from 'three'
import type { ImportedTopologyBounds } from '../loaders/types'

/**
 * bounds 计算的最小有效尺寸。
 *
 * 设计说明：
 * 1. 导入模型常用米制后，很多薄板厚度可能很小，但仍应大于该阈值；
 * 2. 小于阈值的轴不参与比例计算，避免零厚度面把 scale 拉偏；
 * 3. 该阈值只用于单位校准，不参与真实几何裁剪。
 */
const IMPORTED_TOPOLOGY_BOUNDS_EPSILON = 1e-9

/**
 * 可接受的最大相对离散度。
 *
 * 设计说明：
 * 1. BRep 和 GLB 来自同一 body 时，各有效轴的缩放比例应基本一致；
 * 2. 允许 5% 误差用于覆盖不同三角化精度或圆滑曲面的包围盒细微差异；
 * 3. 超出阈值时不盲目缩放，回退到 metadata/default，避免错配 body 被误校准。
 */
const IMPORTED_TOPOLOGY_SCALE_MAX_RELATIVE_SPREAD = 0.05

/**
 * 数值包围盒累加器。
 */
interface ImportedTopologyBoundsAccumulator {
  minX: number
  minY: number
  minZ: number
  maxX: number
  maxY: number
  maxZ: number
  pointCount: number
}

/**
 * 创建空 bounds 累加器。
 *
 * @returns 空 bounds 累加器。
 */
function createImportedTopologyBoundsAccumulator(): ImportedTopologyBoundsAccumulator {
  return {
    minX: Infinity,
    minY: Infinity,
    minZ: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
    maxZ: -Infinity,
    pointCount: 0,
  }
}

/**
 * 向 bounds 累加器写入一个点。
 *
 * @param accumulator bounds 累加器。
 * @param x 点的 x 坐标。
 * @param y 点的 y 坐标。
 * @param z 点的 z 坐标。
 */
function includeImportedTopologyBoundsPoint(
  accumulator: ImportedTopologyBoundsAccumulator,
  x: number,
  y: number,
  z: number,
): void {
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return
  }

  accumulator.minX = Math.min(accumulator.minX, x)
  accumulator.minY = Math.min(accumulator.minY, y)
  accumulator.minZ = Math.min(accumulator.minZ, z)
  accumulator.maxX = Math.max(accumulator.maxX, x)
  accumulator.maxY = Math.max(accumulator.maxY, y)
  accumulator.maxZ = Math.max(accumulator.maxZ, z)
  accumulator.pointCount += 1
}

/**
 * 把 bounds 累加器转换为不可变纯数据。
 *
 * @param accumulator bounds 累加器。
 * @returns 有效包围盒；没有有效点时返回 `undefined`。
 */
function resolveImportedTopologyBounds(
  accumulator: ImportedTopologyBoundsAccumulator,
): ImportedTopologyBounds | undefined {
  if (accumulator.pointCount <= 0) {
    return undefined
  }

  const min: ImportedTopologyBounds['min'] = [
    accumulator.minX,
    accumulator.minY,
    accumulator.minZ,
  ]
  const max: ImportedTopologyBounds['max'] = [
    accumulator.maxX,
    accumulator.maxY,
    accumulator.maxZ,
  ]

  return {
    min,
    max,
    size: [
      max[0] - min[0],
      max[1] - min[1],
      max[2] - min[2],
    ],
  }
}

/**
 * 读取 BufferGeometry 的绘制范围。
 *
 * @param mesh 目标 mesh。
 * @param group 当前 primitive 对应的 geometry group。
 * @returns 起点和数量。
 */
function resolveImportedGeometryRange(
  mesh: THREE.Mesh,
  group?: THREE.BufferGeometry['groups'][number],
): { start: number; count: number; indexed: boolean } {
  const geometry = mesh.geometry
  const indexAttribute = geometry.getIndex()
  if (indexAttribute) {
    return {
      start: group?.start ?? 0,
      count: group?.count ?? indexAttribute.count,
      indexed: true,
    }
  }

  const positionAttribute = geometry.getAttribute('position')
  return {
    start: group?.start ?? 0,
    count: group?.count ?? positionAttribute?.count ?? 0,
    indexed: false,
  }
}

/**
 * 计算 GLB 可见 mesh 单个 primitive 的局部包围盒。
 *
 * 设计说明：
 * 1. 运行时 FeatureResultMesh host 作为 visible mesh 子节点挂载，因此这里使用 mesh local space；
 * 2. 对 indexed geometry 按 index 范围读取，避免把同一 mesh 的其它 primitive 混入当前 body；
 * 3. 该函数只在 loader 阶段执行一次，不进入鼠标事件或渲染循环。
 *
 * @param mesh 可见 GLB mesh。
 * @param group 当前 primitive 的 geometry group。
 * @returns primitive 局部包围盒。
 */
export function computeImportedMeshPrimitiveLocalBounds(
  mesh: THREE.Mesh,
  group?: THREE.BufferGeometry['groups'][number],
): ImportedTopologyBounds | undefined {
  const positionAttribute = mesh.geometry.getAttribute('position')
  if (
    !(positionAttribute instanceof THREE.BufferAttribute)
    && !(positionAttribute instanceof THREE.InterleavedBufferAttribute)
  ) {
    return undefined
  }

  const indexAttribute = mesh.geometry.getIndex()
  const indexArray = indexAttribute?.array
  const range = resolveImportedGeometryRange(mesh, group)
  const accumulator = createImportedTopologyBoundsAccumulator()
  const rangeEnd = Math.min(range.start + range.count, range.indexed ? indexAttribute?.count ?? 0 : positionAttribute.count)

  for (let itemIndex = Math.max(0, range.start); itemIndex < rangeEnd; itemIndex += 1) {
    const vertexIndex = range.indexed
      ? Number(indexArray?.[itemIndex] ?? -1)
      : itemIndex
    if (vertexIndex < 0 || vertexIndex >= positionAttribute.count) {
      continue
    }
    includeImportedTopologyBoundsPoint(
      accumulator,
      positionAttribute.getX(vertexIndex),
      positionAttribute.getY(vertexIndex),
      positionAttribute.getZ(vertexIndex),
    )
  }

  return resolveImportedTopologyBounds(accumulator)
}

/**
 * 合并多个导入拓扑包围盒。
 *
 * @param boundsItems 待合并的包围盒集合。
 * @returns 合并后的包围盒；没有有效项时返回 `undefined`。
 */
export function mergeImportedTopologyBounds(
  boundsItems: Array<ImportedTopologyBounds | undefined>,
): ImportedTopologyBounds | undefined {
  const accumulator = createImportedTopologyBoundsAccumulator()

  boundsItems.forEach((bounds) => {
    if (!bounds) {
      return
    }
    includeImportedTopologyBoundsPoint(accumulator, bounds.min[0], bounds.min[1], bounds.min[2])
    includeImportedTopologyBoundsPoint(accumulator, bounds.max[0], bounds.max[1], bounds.max[2])
  })

  return resolveImportedTopologyBounds(accumulator)
}

/**
 * 计算 BRep MeshData 的局部包围盒。
 *
 * @param meshData worker 生成的 BRep 网格数据。
 * @returns BRep 网格包围盒。
 */
export function computeImportedMeshDataBounds(meshData: Pick<MeshData, 'positions'>): ImportedTopologyBounds | undefined {
  const accumulator = createImportedTopologyBoundsAccumulator()
  const positions = meshData.positions

  for (let index = 0; index + 2 < positions.length; index += 3) {
    includeImportedTopologyBoundsPoint(
      accumulator,
      positions[index] ?? 0,
      positions[index + 1] ?? 0,
      positions[index + 2] ?? 0,
    )
  }

  return resolveImportedTopologyBounds(accumulator)
}

/**
 * 计算相对差异。
 *
 * @param left 左值。
 * @param right 右值。
 * @returns 相对差异。
 */
function computeRelativeDifference(left: number, right: number): number {
  return Math.abs(left - right) / Math.max(Math.abs(left), Math.abs(right), IMPORTED_TOPOLOGY_BOUNDS_EPSILON)
}

/**
 * 解析两个包围盒之间的可靠缩放比例。
 *
 * 设计说明：
 * 1. 只使用两边尺寸都有效的轴，避免薄板零厚度轴污染比例；
 * 2. 多轴比例必须足够一致，否则说明 body 绑定或 bounds 不可靠；
 * 3. 返回值是 BRep 坐标乘上后进入 visible mesh local space 的比例。
 *
 * @param visibleBounds GLB 可见几何包围盒。
 * @param brepBounds BRep 网格包围盒。
 * @returns 可靠缩放比例；无法确认时返回 `undefined`。
 */
export function resolveImportedTopologyBoundsScale(
  visibleBounds: ImportedTopologyBounds | undefined,
  brepBounds: ImportedTopologyBounds | undefined,
): number | undefined {
  if (!visibleBounds || !brepBounds) {
    return undefined
  }

  const ratios: number[] = []
  for (let axis = 0; axis < 3; axis += 1) {
    const visibleSize = visibleBounds.size[axis] ?? 0
    const brepSize = brepBounds.size[axis] ?? 0
    if (visibleSize <= IMPORTED_TOPOLOGY_BOUNDS_EPSILON || brepSize <= IMPORTED_TOPOLOGY_BOUNDS_EPSILON) {
      continue
    }
    const ratio = visibleSize / brepSize
    if (Number.isFinite(ratio) && ratio > 0) {
      ratios.push(ratio)
    }
  }

  if (ratios.length < 2) {
    return undefined
  }

  const sortedRatios = [...ratios].sort((left, right) => left - right)
  const medianRatio = sortedRatios[Math.floor(sortedRatios.length / 2)]
  if (!Number.isFinite(medianRatio) || medianRatio <= 0) {
    return undefined
  }

  const isScaleConsistent = sortedRatios.every(
    (ratio) => computeRelativeDifference(ratio, medianRatio) <= IMPORTED_TOPOLOGY_SCALE_MAX_RELATIVE_SPREAD,
  )
  return isScaleConsistent ? medianRatio : undefined
}
